import { Howl } from 'howler';
import type { AudioCue } from '../types/slide-doc';
import { playStinger, type StingerName } from './stingerSynth';

// Cue source forms:
//   '/audio/foo.mp3'        → Howler-backed file
//   'synth:logo-reveal'     → Web Audio synthesized stinger (royalty-free)
type ScheduledCue =
  | { kind: 'file'; cue: AudioCue; howl: Howl; timer: number | null }
  | { kind: 'synth'; cue: AudioCue; name: StingerName; timer: number | null; handle: { stop: () => void } | null };

export class SlideAudioController {
  private cues: ScheduledCue[] = [];

  load(cues: AudioCue[]): Promise<void> {
    this.cues = cues.map((cue): ScheduledCue => {
      if (cue.src.startsWith('synth:')) {
        return {
          kind: 'synth',
          cue,
          name: cue.src.slice('synth:'.length) as StingerName,
          timer: null,
          handle: null,
        };
      }
      return {
        kind: 'file',
        cue,
        howl: new Howl({ src: [cue.src], volume: cue.volume ?? 1, loop: cue.loop ?? false }),
        timer: null,
      };
    });
    return Promise.all(
      this.cues.map(
        (c) =>
          new Promise<void>((resolve) => {
            if (c.kind !== 'file') return resolve();
            if (c.howl.state() === 'loaded') return resolve();
            c.howl.once('load', () => resolve());
            c.howl.once('loaderror', () => resolve());
          })
      )
    ).then(() => undefined);
  }

  start() {
    const t0 = performance.now();
    for (const c of this.cues) {
      const delay = Math.max(0, c.cue.atMs - (performance.now() - t0));
      c.timer = window.setTimeout(() => {
        if (c.kind === 'file') c.howl.play();
        else c.handle = playStinger(c.name, c.cue.volume ?? 0.9);
      }, delay);
    }
  }

  stop() {
    for (const c of this.cues) {
      if (c.timer != null) window.clearTimeout(c.timer);
      if (c.kind === 'file') c.howl.stop();
      else c.handle?.stop();
    }
  }

  unload() {
    this.stop();
    for (const c of this.cues) {
      if (c.kind === 'file') c.howl.unload();
    }
    this.cues = [];
  }
}
